import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionList, RefreshControl, Platform, Alert } from 'react-native';

import EmptyStateNoFiles from '@components/EmptyState/EmptyStateNoFiles.web';
import { NotPaidConfirmModal } from '@components/Modals/NotPaidConfirmModal';
import { Box } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import ImageGridNew from '@components/shared/ImageGridNew.web';
import { PopupTaskFilesFilterButtonWeb } from '@components/Web/PopupTaskFilesFilterButton.web';
import {
  Document,
  DocumentSortOption,
  LocalFile,
  Task,
  TaskMember,
} from '@graphql/generated';
import { useListTaskDocumentsFromQuery } from '@hooks/useListTaskDocumentsFromQuery';
import useMe from '@hooks/useMe';
import { usePreviewDocuments } from '@hooks/usePreviewDocuments';
import { ImagesPreview } from '@screens/Chats/ImagesPreview.web';
import { FilterType } from '@screens/Projects/ProjectFiles';
import theme from '@themes/theme';

interface TaskFilesAndMediaProps {
  taskId: Task['id'];
  taskMembers: TaskMember[];
  sortBy?: DocumentSortOption;
  isSelectMode: boolean;
  setIsSelectMode: React.Dispatch<React.SetStateAction<boolean>>;
  setIsShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectedModeChange: (isSelected: boolean) => void;
  selectedList: Array<Document | LocalFile>;
  handelSelectedItem: (item: Document | LocalFile) => void;
  onChangeSelectedList?: (next: Array<Document | LocalFile>) => void;
  addFileOrMedia?: () => void;
  onRetry?: (image: Document | LocalFile) => void;
  isNotViewer?: boolean;
}

const TaskFilesAndMedia: React.FC<TaskFilesAndMediaProps> = ({
  taskId,
  taskMembers,
  sortBy,
  isSelectMode,
  setIsSelectMode,
  setIsShowFilter,
  onSelectedModeChange,
  selectedList,
  handelSelectedItem,
  onChangeSelectedList,
  addFileOrMedia,
  onRetry,
  isNotViewer = false,
}) => {
  const { setPreviewDocuments, setActiveIndex } = usePreviewDocuments();
  const [isImageCarouselVisible, setImageCarouselVisible] = useState(false);
  const { t } = useTranslation('format');
  const { isPaid, setPaid, me } = useMe(); // Filter members based on search uploader text
  const [filterBy, setFilterBy] = useState<FilterType>(FilterType.NoFilter);
  const [filterMedia, setFilterMedia] = useState(false);
  const [filterDocument, setFilterDocument] = useState(false);
  const [searchUploader, setSearchUploader] = useState('');

  const handleFilterMediaChange = (selected: boolean) => {
    setFilterMedia(selected);
  };

  const handleFilterDocumentChange = (selected: boolean) => {
    setFilterDocument(selected);
  };

  const handleSearchChange = (searchText: string) => {
    setSearchUploader(searchText);
  };

  const handleClearAllFilters = () => {
    setFilterMedia(false);
    setFilterDocument(false);
    setSearchUploader('');
    setFilterBy(FilterType.NoFilter);
  };

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterBy !== FilterType.NoFilter) count++;
    if (filterMedia) count++;
    if (filterDocument) count++;
    if (searchUploader.trim()) count++;
    return count;
  }, [filterBy, filterMedia, filterDocument, searchUploader]);

  const getFilteredOwnerIds = useMemo(() => {
    // If MyUploads filter is active, use current user ID
    if (filterBy === FilterType.MyUploads && me?.id) {
      return [me.id];
    }

    // If search text is provided, filter members by name
    if (searchUploader.trim()) {
      const searchLower = searchUploader.toLowerCase().trim();
      const matchingMembers = taskMembers.filter((member) => {
        const user = member?.user;
        if (!user) return false;

        const name = user.name || '';

        return name.toLowerCase().includes(searchLower);
      });

      // Extract user IDs from matching members
      const matchingIds = matchingMembers
        .map((member) => member?.user?.id)
        .filter((id): id is string => !!id);

      // If search text exists but no members match, return ['0'] to filter out all documents
      if (matchingIds.length === 0) {
        return ['0'];
      }

      return matchingIds;
    }

    // No filter applied
    return undefined;
  }, [taskMembers, searchUploader, filterBy, me?.id]);

  // Determine includeMedia and includeFiles based on filter selection
  // If Media only selected: includeMedia: true
  // If Document only selected: includeFiles: true
  // If both or none selected: don't pass anything (show all)
  const getIncludeParams = () => {
    const onlyMedia = filterMedia && !filterDocument;
    const onlyDocument = filterDocument && !filterMedia;

    if (onlyMedia) {
      return { includeMedia: true };
    } else if (onlyDocument) {
      return { includeFiles: true };
    }
    // If both selected or none selected, don't pass anything (show all)
    return {};
  };

  const {
    documents,
    loading,
    refetch,
    pageInfo,
    fetchFromCursor,
    refreshing,
    isFetchingMore,
  } = useListTaskDocumentsFromQuery({
    taskIds: [taskId],
    ownerIds: getFilteredOwnerIds,
    sortBy,
    ...getIncludeParams(),
  });

  const [sortedDocuments, setSortedDocuments] = useState<Document[]>([]);

  const [isPaidUser, setPaidUser] = useState<boolean>(!!isPaid);
  const [showNotPaidConfirmModal, setShowNotPaidConfirmModal] =
    useState<boolean>(false);

  const showNotPaidModal = (file: Document) => {
    const createdAt = dayjs(file.createdAt);
    const expired = createdAt?.diff(new Date(), 'day');
    if (!isPaidUser && expired < -1000) {
      if (Platform.OS === 'web') {
        setShowNotPaidConfirmModal(true);
      } else {
        Alert.alert(
          'Task ' + t('shared:attachmentsExpired'),
          t('shared:attachmentsExpiredMessage'),
          [
            { text: 'Cancel' },
            {
              text: t('shared:upgrade'),
              style: 'destructive',
              onPress: () => {
                setPaid(true);
                setPaidUser(true);
              },
            },
          ]
        );
      }
      return true;
    }
    return false;
  };

  useEffect(() => {
    const sortedDocuments = documents.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    setSortedDocuments(sortedDocuments);
  }, [setSortedDocuments, documents]);

  useEffect(() => {
    if (!loading && pageInfo?.hasNextPage && !isFetchingMore) {
      setTimeout(() => {
        fetchFromCursor();
      }, 100);
    }
  }, [documents.length, loading, pageInfo?.hasNextPage, isFetchingMore]);

  // Keep the open viewer in sync with the paginated query results so newly
  // fetched pages extend the viewer's navigation set (TA-9771). Synced by id
  // signature — `sortedDocuments` gets a new identity on every query render,
  // so syncing on identity would loop (setPreviewDocuments → context render →
  // new array → effect).
  const sortedIdsSignature = useMemo(
    () => sortedDocuments.map((d) => d.id).join(','),
    [sortedDocuments]
  );
  useEffect(() => {
    if (!isImageCarouselVisible) return;
    setPreviewDocuments(sortedDocuments);
  }, [sortedIdsSignature, isImageCarouselVisible]);

  const renderItem = ({ item }: { item: Document[] }) => {
    return (
      <Box marginRight='xs' marginBottom='xs'>
        <ImageGridNew
          list={item}
          isSelectMode={isSelectMode}
          setIsSelectMode={setIsSelectMode}
          selectedList={selectedList}
          onSelect={(image) => {
            if (showNotPaidModal(image as Document)) return;
            handelSelectedItem(image);
          }}
          onChangeSelectedList={onChangeSelectedList}
          onPress={(image) => {
            if (showNotPaidModal(image as Document)) return;
            const images = item;
            setPreviewDocuments(images);
            setActiveIndex(images.findIndex((i) => i.id === image.id));
            setImageCarouselVisible(true);
          }}
          onLongPress={(image) => {
            onSelectedModeChange(true);
            handelSelectedItem(image);
          }}
          onRetry={onRetry}
        />
      </Box>
    );
  };

  const renderListFooter = useCallback(
    () => (isFetchingMore ? <ActivityIndicatorLoading /> : <Box />),
    [isFetchingMore]
  );

  return (
    <>
      {/* Filter Button */}
      {(documents.length > 0 || loading || activeFilterCount > 0) && (
        <Box width={activeFilterCount === 0 ? 108 : 142} my='s'>
          <PopupTaskFilesFilterButtonWeb
            onSelectMedia={handleFilterMediaChange}
            onSelectDocument={handleFilterDocumentChange}
            onSearchChange={handleSearchChange}
            onClearAll={handleClearAllFilters}
            mediaSelected={filterMedia}
            documentSelected={filterDocument}
            searchValue={searchUploader}
            filterCount={activeFilterCount}
            setIsShowFilter={setIsShowFilter}
            boxProps={{
              position: 'bottom',
              offset: theme.spacing.s,
            }}
          />
        </Box>
      )}

      <Box flex={1} testID='task-media' paddingTop='m'>
        {loading && documents.length === 0 && (
          <Box mb='listFooter'>
            <ActivityIndicatorLoading />
          </Box>
        )}
        {/* Render empty state outside SectionList — the SectionList scroll
            container on web intercepts pointer events and prevents clicks
            inside ListEmptyComponent from reaching their handlers. */}
        {!loading && documents.length === 0 && isNotViewer && (
          <EmptyStateNoFiles onPress={addFileOrMedia} />
        )}
        {documents.length > 0 && (
          <SectionList
            showsVerticalScrollIndicator={false}
            stickySectionHeadersEnabled={false}
            sections={[{ title: '', data: [sortedDocuments] }]}
            renderItem={renderItem}
            onEndReachedThreshold={0.5}
            onEndReached={() => {
              if (!pageInfo?.hasNextPage || isFetchingMore) return;
              fetchFromCursor();
            }}
            ListFooterComponent={renderListFooter}
            contentContainerStyle={{ flexGrow: 1 }}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={refetch} />
            }
          />
        )}
        {Platform.OS === 'web' && isImageCarouselVisible && (
          <ImagesPreview
            supportNonImageFiles
            onDismiss={() => setImageCarouselVisible(false)}
            hasNextPage={!!pageInfo?.hasNextPage}
            loadingMore={isFetchingMore}
            onLoadMore={() => {
              if (!pageInfo?.hasNextPage || isFetchingMore) return;
              fetchFromCursor();
            }}
          />
        )}
        {showNotPaidConfirmModal && (
          <NotPaidConfirmModal
            showModal={showNotPaidConfirmModal}
            onClose={() => setShowNotPaidConfirmModal(false)}
            onPress={() => {
              setShowNotPaidConfirmModal(false);
              setPaid(true);
              setPaidUser(true);
            }}
            title='Task'
          />
        )}
      </Box>
    </>
  );
};

export default TaskFilesAndMedia;
